import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  BACKOFFICE_NAVIGATION_ITEMS,
  type BackofficeNavigationItem,
  type BackofficeNavigationItemId,
  type BackofficeNavigationTopLevelItemId,
} from '@/components/layout/backoffice-navigation-items';
import { fetchBackofficePreferences, upsertBackofficePreference } from './backoffice-preferences';

export interface BackofficeNavigationPreferenceValue extends Record<string, unknown> {
  orderedItemIds: BackofficeNavigationTopLevelItemId[];
  hiddenItemIds: BackofficeNavigationItemId[];
}

interface BackofficeNavigationPreferenceContextValue {
  hiddenItemIds: Set<BackofficeNavigationItemId>;
  loaded: boolean;
  loadError: Error | null;
  orderedItemIds: BackofficeNavigationTopLevelItemId[];
  orderedItems: BackofficeNavigationItem[];
  saveNavigationPreference: (value: BackofficeNavigationPreferenceValue) => Promise<void>;
  visibleItems: BackofficeNavigationItem[];
  resetNavigationPreference: () => void;
}

const BACKOFFICE_NAVIGATION_PREFERENCE_SCOPE = 'navigation:menu';
const BACKOFFICE_NAVIGATION_PREFERENCE_SCHEMA_VERSION = 3;

export const DEFAULT_BACKOFFICE_NAVIGATION_PREFERENCE: BackofficeNavigationPreferenceValue = {
  orderedItemIds: [
    'organizations',
    'users',
    'studies',
    'interviews',
    'vouchers',
    'links',
    'tags',
    'developer',
  ],
  hiddenItemIds: ['tags', 'developer', 'developer-messagebus', 'developer-observability'],
};

const navigationItemIds = BACKOFFICE_NAVIGATION_ITEMS.flatMap((item) => [
  item.id,
  ...(item.children?.map((child) => child.id) ?? []),
]);
const navigationItemIdSet = new Set<BackofficeNavigationItemId>(navigationItemIds);
const topLevelNavigationItemIds = BACKOFFICE_NAVIGATION_ITEMS.map((item) => item.id);
const navigationItemsById = new Map(
  BACKOFFICE_NAVIGATION_ITEMS.map(
    (item) =>
      [item.id, item] satisfies [BackofficeNavigationTopLevelItemId, BackofficeNavigationItem]
  )
);

const BackofficeNavigationPreferenceContext =
  createContext<BackofficeNavigationPreferenceContextValue | null>(null);

export function resolveBackofficeNavigationPreference(
  value: Partial<BackofficeNavigationPreferenceValue> | null | undefined,
  schemaVersion = BACKOFFICE_NAVIGATION_PREFERENCE_SCHEMA_VERSION
): BackofficeNavigationPreferenceValue {
  const seenOrdered = new Set<BackofficeNavigationTopLevelItemId>();
  const orderedItemIds: BackofficeNavigationTopLevelItemId[] = [];

  for (const itemId of value?.orderedItemIds ?? []) {
    if (!navigationItemsById.has(itemId) || seenOrdered.has(itemId)) {
      continue;
    }
    seenOrdered.add(itemId);
    orderedItemIds.push(itemId);
  }

  for (const itemId of DEFAULT_BACKOFFICE_NAVIGATION_PREFERENCE.orderedItemIds) {
    if (seenOrdered.has(itemId)) {
      continue;
    }
    seenOrdered.add(itemId);
    orderedItemIds.push(itemId);
  }

  for (const itemId of topLevelNavigationItemIds) {
    if (seenOrdered.has(itemId)) {
      continue;
    }
    seenOrdered.add(itemId);
    orderedItemIds.push(itemId);
  }

  const hiddenSeen = new Set<BackofficeNavigationItemId>();
  const hiddenItemIds: BackofficeNavigationItemId[] = [];

  for (const itemId of value?.hiddenItemIds ??
    DEFAULT_BACKOFFICE_NAVIGATION_PREFERENCE.hiddenItemIds) {
    if (!navigationItemIdSet.has(itemId) || hiddenSeen.has(itemId)) {
      continue;
    }
    hiddenSeen.add(itemId);
    hiddenItemIds.push(itemId);
  }

  if (schemaVersion < 2) {
    for (const itemId of [
      'developer',
      'developer-messagebus',
    ] satisfies BackofficeNavigationItemId[]) {
      if (!hiddenSeen.has(itemId)) {
        hiddenSeen.add(itemId);
        hiddenItemIds.push(itemId);
      }
    }
  }

  if (schemaVersion < 3 && !hiddenSeen.has('developer-observability')) {
    hiddenSeen.add('developer-observability');
    hiddenItemIds.push('developer-observability');
  }

  return {
    orderedItemIds,
    hiddenItemIds,
  };
}

export function BackofficeNavigationPreferenceProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<BackofficeNavigationPreferenceValue>(
    DEFAULT_BACKOFFICE_NAVIGATION_PREFERENCE
  );
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    setLoaded(false);
    setLoadError(null);

    fetchBackofficePreferences([BACKOFFICE_NAVIGATION_PREFERENCE_SCOPE], controller.signal)
      .then((preferences) => {
        const preference = preferences.find(
          (item) => item.scope === BACKOFFICE_NAVIGATION_PREFERENCE_SCOPE
        );
        setValue(
          resolveBackofficeNavigationPreference(preference?.value, preference?.schemaVersion)
        );
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }

        setValue(DEFAULT_BACKOFFICE_NAVIGATION_PREFERENCE);
        setLoadError(
          caught instanceof Error ? caught : new Error('Failed to load navigation preference.')
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoaded(true);
        }
      });

    return () => controller.abort();
  }, []);

  const saveNavigationPreference = useCallback(
    async (nextValue: BackofficeNavigationPreferenceValue) => {
      const resolved = resolveBackofficeNavigationPreference(nextValue);

      await upsertBackofficePreference(
        BACKOFFICE_NAVIGATION_PREFERENCE_SCOPE,
        resolved,
        BACKOFFICE_NAVIGATION_PREFERENCE_SCHEMA_VERSION
      );

      setValue(resolved);
    },
    []
  );

  const resetNavigationPreference = useCallback(() => {
    setValue(DEFAULT_BACKOFFICE_NAVIGATION_PREFERENCE);
  }, []);

  const contextValue = useMemo<BackofficeNavigationPreferenceContextValue>(() => {
    const hiddenItemIds = new Set(value.hiddenItemIds);
    const orderedItems = value.orderedItemIds
      .map((itemId) => navigationItemsById.get(itemId))
      .filter((item): item is BackofficeNavigationItem => item !== undefined);

    return {
      hiddenItemIds,
      loaded,
      loadError,
      orderedItemIds: value.orderedItemIds,
      orderedItems,
      saveNavigationPreference,
      visibleItems: orderedItems
        .filter((item) => !hiddenItemIds.has(item.id))
        .map((item) => ({
          ...item,
          ...(item.children
            ? { children: item.children.filter((child) => !hiddenItemIds.has(child.id)) }
            : {}),
        })),
      resetNavigationPreference,
    };
  }, [loadError, loaded, resetNavigationPreference, saveNavigationPreference, value]);

  return (
    <BackofficeNavigationPreferenceContext.Provider value={contextValue}>
      {children}
    </BackofficeNavigationPreferenceContext.Provider>
  );
}

export function useBackofficeNavigationPreference(): BackofficeNavigationPreferenceContextValue {
  const context = useContext(BackofficeNavigationPreferenceContext);

  if (!context) {
    throw new Error(
      'useBackofficeNavigationPreference must be used within BackofficeNavigationPreferenceProvider.'
    );
  }

  return context;
}
